import type {
  DocumentData,
  DocumentReference,
  Query,
  QuerySnapshot,
  QueryDocumentSnapshot,
  DocumentSnapshot,
} from 'firebase/firestore'
import type { CollectionReference } from 'firebase/firestore'
import { documentId, onSnapshot, query, where } from 'firebase/firestore'
import type { FirebaseRepository } from '../models/FirebaseRepository'
import type { MobxDocument } from '../types'
import { StreamController } from 'tricklejs'
import type { StreamInterface } from 'tricklejs/dist/types'
import type { StreamSubscriptionActions } from 'tricklejs/dist/stream_subscription'
import type { ObservableModel, ObservableModelClass } from './model'
import { captureException } from '@sentry/core'
import { WrappedFirestoreError } from './errors'
import { partition, unique } from '../util/arrays'

const CHROME_IE_STACK_REGEXP = /^\s*at .*(\S+:\d+|\(native\))/m

let docCounter = 0
let docCounterActive = false
export const startDocumentCounter = () => {
  docCounterActive = true
}

let timeout: NodeJS.Timeout | null = null
const addDocumentCount = (count: number) => {
  if (!docCounterActive) return
  if (timeout) clearTimeout(timeout)
  docCounter += count
  timeout = setTimeout(() => {
    // eslint-disable-next-line no-console
    console.log(`Stream doc changes: ${docCounter}`)
    docCounter = 0
  }, 1000)
}

// This function removes the lines related to the new Error().stack trick
// we do to get around Firestore's insane error handling
function sanitizeStack(
  stack: string | undefined,
  options?: { wrapped: boolean }
) {
  if (!stack) return undefined
  const linesToRemove = options?.wrapped ? 2 : 1
  const lines = stack.split('\n')
  // In Chrome/Edge
  //
  // when wrapped, we want to remove 2 lines
  //  - line 1 is "Error:"
  //  - line 2 is in collectionSnapshots or documentSnapshots
  //  - line 3 is the wrapper function -> only remove if wrapped
  // So we remove lines 2 and/or 3
  //
  // when not wrapped, we want to remove 1 or 2 lines
  //  - line 1 is in collectionSnapshots or documentSnapshots
  //  - line 2 is the wrapper function -> only remove if wrapped
  //
  // In other browsers, we want to remove 2 or 1 lines, depending on whether
  // it's wrapped or not
  if (stack.match(CHROME_IE_STACK_REGEXP)) {
    lines.splice(1, linesToRemove)
  } else {
    lines.splice(0, linesToRemove)
  }
  return lines.join('\n')
}

/**
 * Replacement for .snapshots() in Dart
 * @param {boolean | undefined} options.skipUndefinedCacheResults - do not add snapshots to the stream if they are from cache and the document does not exist
 */
export function documentSnapshots<T>(
  ref: DocumentReference<T, DocumentData>,
  options?: {
    wrapped: boolean
    skipUndefinedCacheResults?: boolean
    ignorePermissionErrors?: boolean
  }
) {
  const controller = new StreamController<DocumentSnapshot<T, DocumentData>>()

  const stack = sanitizeStack(new Error().stack, options)

  let disposer: () => void = () => {}

  controller.onListen = () => {
    disposer = onSnapshot(
      ref,
      (snapshot) => {
        if (docCounterActive) addDocumentCount(1)
        const { fromCache } = snapshot.metadata
        const skipUndefinedCacheResults =
          options?.skipUndefinedCacheResults || false

        // wait for update from server before adding document to the stream with no data
        if (skipUndefinedCacheResults && fromCache && !snapshot.exists()) return

        controller.add(snapshot)
      },
      (error) => {
        const err = new WrappedFirestoreError(
          error.message || 'Firestore error',
          error.code
        )
        err.name = 'WrappedFirestoreError'
        err.stack = stack
        controller.addError(err)
        const isPermissionError = error.code === 'permission-denied'
        if (!isPermissionError || !options?.ignorePermissionErrors) {
          setTimeout(() => {
            captureException(err)
          }, 250) // give the handler 250ms to handle the error
        }
      }
    )
  }

  controller.onCancel = () => {
    disposer()
  }

  return controller.stream
}

/**
 * Replacement for .snapshots() in Dart
 */
export function collectionSnapshots<T>(
  ref: CollectionReference<T, DocumentData> | Query<T>,
  options?: {
    wrapped: boolean
    ignorePermissionErrors?: boolean
    includeMetadataChanges?: boolean
  }
) {
  const controller = new StreamController<QuerySnapshot<T, DocumentData>>()

  let disposer: () => void = () => {}
  const stack = sanitizeStack(new Error().stack, options)

  controller.onListen = () => {
    disposer = onSnapshot(
      ref,
      { includeMetadataChanges: options?.includeMetadataChanges ?? false },
      (snapshot) => {
        if (docCounterActive) {
          const docChangeLength = snapshot.docChanges().length
          if (docChangeLength > 0) {
            addDocumentCount(docChangeLength)
          } else {
            addDocumentCount(snapshot.docs.length)
          }
        }
        controller.add(snapshot)
      },
      (error) => {
        const err = new WrappedFirestoreError(
          error.message || 'Firestore error',
          error.code
        )
        err.name = 'WrappedFirestoreError'
        err.stack = stack
        controller.addError(err)
        const isPermissionError = error.code === 'permission-denied'
        if (!isPermissionError || !options?.ignorePermissionErrors) {
          setTimeout(() => {
            captureException(err)
          }, 250) // give the handler 250ms to handle the error
        }
      }
    )
  }

  controller.onCancel = () => {
    disposer()
  }

  return controller.stream
}

export function convertDocumentSnapshotToModel<
  T extends DocumentData,
  M extends ObservableModel<T>,
>(
  repository: FirebaseRepository,
  doc: DocumentSnapshot<T> | QueryDocumentSnapshot<T>,
  modelClass: ObservableModelClass<T, M>
) {
  const mobxDoc = {
    id: doc.id,
    ref: doc.ref,
    fromCache: doc.metadata.fromCache,
    hasPendingWrites: doc.metadata.hasPendingWrites,
    exists: doc.exists(),
    data: doc.data({
      serverTimestamps: 'estimate',
    }),
  } as MobxDocument<T>
  return new modelClass(repository, mobxDoc)
}

/**
 * Convenience function to turn a Firestore doc into a model
 */
export function modelItemStream<
  T extends DocumentData,
  M extends ObservableModel<T>,
>(
  repository: FirebaseRepository,
  ref: DocumentReference<T>,
  modelClass: ObservableModelClass<T, M>,
  options?: { ignorePermissionErrors?: boolean }
): StreamInterface<M> {
  const result = documentSnapshots(ref, {
    wrapped: true,
    skipUndefinedCacheResults: true,
    ignorePermissionErrors: options?.ignorePermissionErrors,
  }).map((snapshot) => {
    // if (docCounterActive) addDocumentCount(1)
    return convertDocumentSnapshotToModel(repository, snapshot, modelClass)
  })
  return result
}

/**
 * Convenience method to turn a list of Firestore docs into a list of models
 */
export function modelListStream<
  T extends DocumentData,
  M extends ObservableModel<T>,
>(
  repository: FirebaseRepository,
  ref: CollectionReference<T> | Query<T>,
  modelClass: ObservableModelClass<T, M>,
  options?: {
    ignorePermissionErrors?: boolean
    includeMetadataChanges?: boolean
  }
): StreamInterface<M[]> {
  const result = collectionSnapshots(ref, {
    wrapped: true,
    ignorePermissionErrors: options?.ignorePermissionErrors,
    includeMetadataChanges: options?.includeMetadataChanges,
  }).map((snapshot) => {
    // if (docCounterActive) addDocumentCount(snapshot.docChanges().length)
    const models = snapshot.docs.map((doc) => {
      return convertDocumentSnapshotToModel(repository, doc, modelClass)
    })
    return models
  })
  return result
}

/**
 * Creates a stream of partitioned Firestore queries when the number of items in `matchArray`
 * exceeds the limit for comparisons in a `where in` query. This function partitions the array
 * and runs multiple Firestore queries, collecting their results into a single stream.
 *
 * @param params.repository - The repository containing Firestore connection information.
 * @param params.ref - The Firestore collection reference to query or collectionGroup reference to query.
 * @param params.matchArray - Array of values to match against in the Firestore `where in` clause.
 * @param params.model - The model class for transforming Firestore documents into observable models.
 * @param params.maxPartitionLength - The maximum number of elements allowed in a single `where in` query (Default 15)
 * @param params.pruneEmptyStrings - Whether to exclude empty strings from the matchArray (Default true)
 * @param params.queryBuilder - A function to build a Firestore query for each partition. Defaults to a `where` query on the `id` field.
 * @param params.streamBuilder - A function to create a stream for each partitioned query. Defaults to using `modelListStream`.
 *
 * @example
 * // Example usage:
 * const userStream = partitionedQueryStream<PublicUser, PublicUserModel>({
 *   repository,
 *   ref: userCollectionRef,
 *   matchArray: userIds,
 *   model: PublicUserModel,
 *   maxPartitionLength: 10,
 *   queryBuilder: (ref, part) => query(ref, where(documentId(), 'in', part)),
 *   streamBuilder: (repository, q, model) => modelListStream(repository, q, model)
 * });
 */
export function partitionedQueryStream<
  T extends DocumentData,
  M extends ObservableModel<T>,
>({
  repository,
  ref,
  matchArray,
  model,
  maxPartitionLength = 15,
  pruneEmptyStrings = true,
  queryBuilder = (ref, part) => query(ref, where(documentId(), 'in', part)),
  streamBuilder = (repository, query, model) =>
    modelListStream(repository, query, model),
}: {
  repository: FirebaseRepository
  ref: CollectionReference<T> | Query<T>
  matchArray: Array<string | number | boolean>
  model: ObservableModelClass<T, M>
  maxPartitionLength?: number
  pruneEmptyStrings?: boolean
  queryBuilder?: (
    ref: CollectionReference<T> | Query<T>,
    part: typeof matchArray
  ) => Query<T>
  streamBuilder?: (
    repository: FirebaseRepository,
    query: Query<T>,
    model: ObservableModelClass<T, M>
  ) => StreamInterface<M[]>
}): ReturnType<typeof streamBuilder> {
  if (matchArray.length === 0) {
    const controller = new StreamController<M[]>()
    controller.add([])
    controller.close()
    return controller.stream
  }
  const uniqueMatchArray = unique(matchArray).filter((s) => {
    if (typeof s === 'string' && pruneEmptyStrings) return s.length > 0
    return true
  })

  // If the array is small not small enough to partition, just run a single query
  // and avoid constructing the stream collector
  if (uniqueMatchArray.length <= maxPartitionLength) {
    const q = queryBuilder(ref, uniqueMatchArray)
    return streamBuilder(repository, q, model)
  }

  const collector = new CollectionSnapshotStreamCollector<M>({
    resolveAfterAllLoaded: true,
  })

  const partitions = partition(uniqueMatchArray, maxPartitionLength)

  partitions.forEach((part) => {
    const q = queryBuilder(ref, part)
    const stream = streamBuilder(repository, q, model)
    collector.attachStream(stream)
  })

  return collector.stream
}

export class CollectionSnapshotStreamCollector<T> {
  controller: StreamController<T[]>
  subscriptions: StreamSubscriptionActions[] = []
  streams: StreamInterface<T[]>[] = []
  streamResults = new Map<StreamInterface<T[]>, T[]>()
  resolveAfterAllLoaded: boolean

  constructor({
    resolveAfterAllLoaded,
  }: { resolveAfterAllLoaded?: boolean } = {}) {
    this.controller = new StreamController<T[]>()
    this.controller.onCancel = () => this.close()
    this.resolveAfterAllLoaded = resolveAfterAllLoaded || false
  }

  get stream(): StreamInterface<T[]> {
    return this.controller.stream
  }

  close() {
    this.subscriptions.forEach((s) => s.cancel())
    this.controller.close()
  }

  /**
   * @param options.valuesOnError - when set, an error on this stream resolves
   * it to these values instead of leaving it unresolved
   */
  attachStream(
    stream: StreamInterface<T[]>,
    options: { valuesOnError?: T[] } = {}
  ) {
    this.streams.push(stream)
    const { valuesOnError } = options
    const subscription = stream.listen(
      (values) => {
        this.streamResults.set(stream, values)
        this.flush()
      },
      valuesOnError === undefined
        ? undefined
        : {
            onError: () => {
              this.streamResults.set(stream, valuesOnError)
              this.flush()
            },
          }
    )
    this.subscriptions.push(subscription)
  }

  flush() {
    let allStreamsResolved = true
    const values = this.streams
      .map((s) => {
        const resultsForStream = this.streamResults.get(s)
        if (resultsForStream === undefined) allStreamsResolved = false
        return this.streamResults.get(s) || []
      })
      .flat()

    // waiting for all streams to resolve before adding to the controller
    // makes it such that it operates like a single stream
    if (this.resolveAfterAllLoaded && !allStreamsResolved) return

    this.controller.add(values)
  }
}
